// src/contexts/CartContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Cart, AddToCartDto, UpdateCartItemDto } from '../types';
import { cartApi } from '../api';
import translations, { Language } from '../translations';

const getT = (key: string) => {
  const lang = (localStorage.getItem('appLanguage') || 'ar') as Language;
  return translations[lang]?.[key] || translations['ar']?.[key] || key;
};

interface CartContextType {
  cart: Cart | null;
  loading: boolean;
  error: string | null;
  addToCart: (data: AddToCartDto) => Promise<void>;
  updateCartItem: (id: string, data: UpdateCartItemDto) => Promise<void>;
  removeFromCart: (id: string) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
  getItemCount: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshCart = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await cartApi.getCart();
      setCart(response.data.data);
    } catch (err: any) {
      console.error('Error loading cart:', err);
      // Don't set error for empty cart
      setCart({ items: [], total: '0.00', itemCount: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  const addToCart = async (data: AddToCartDto) => {
    try {
      setLoading(true);
      setError(null);
      await cartApi.addToCart(data);
      await refreshCart();
    } catch (err: any) {
      setError(err.response?.data?.message || getT('cart.addFailed'));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateCartItem = async (id: string, data: UpdateCartItemDto) => {
    try {
      setLoading(true);
      setError(null);
      await cartApi.updateCartItem(id, data);
      await refreshCart();
    } catch (err: any) {
      setError(err.response?.data?.message || getT('cart.updateFailed'));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      await cartApi.removeFromCart(id);
      await refreshCart();
    } catch (err: any) {
      setError(err.response?.data?.message || getT('cart.removeFailed'));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      setLoading(true);
      setError(null);
      await cartApi.clearCart();
      setCart({ items: [], total: '0.00', itemCount: 0 });
    } catch (err: any) {
      setError(err.response?.data?.message || getT('cart.clearFailed'));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getItemCount = () => {
    return cart?.itemCount || 0;
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        error,
        addToCart,
        updateCartItem,
        removeFromCart,
        clearCart,
        refreshCart,
        getItemCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};